import { Static, Type } from '@sinclair/typebox';
import { ProviderSchema } from '../../common/provider.schema';
import { MinimalUser, minimalUserSchema } from '../../shared/user/user.schema';
import { StringEnum } from '../../utils/typebox';
import { GroupParticipant, groupParticipantSchema } from '../group-participant/group-participant.schema';
import { Message, MessagePreview, messagePreviewSchema, messageSchema } from '../message/message.schema';

export const chatTypeSchema = StringEnum(['group', '1to1', 'channel'], {
  description: `The type of the chat.
    - \`group\` is a group chat.
    - \`1to1\` is a 1to1 chat.
    - \`channel\` is a channel chat.`,
});

export const chatPermissionSchema = StringEnum(['send_message']);

export const chatSchema = Type.Object({
  object: Type.Literal('Chat'),
  id: Type.String({ description: 'The unique identifier of the chat for the provider.' }),
  name: Type.String({
    description:
      'The name / title of the chat. If the provider does not provide a name, name is built out of participants usernames.',
  }),
  label: Type.Optional(
    Type.String({
      description: 'If supported by the provider, a label associated with the chat.',
    }),
  ),
  description: Type.Optional(Type.String({ description: 'The description / subject of the chat.' })),
  public_picture_url: Type.Optional(Type.String({ format: 'uri', description: 'Public URL of the chat picture.' })),
  private_picture_download_url: Type.Optional(
    Type.String({ format: 'uri', description: 'Private URL to download the chat picture. This URL requires authentication.' }),
  ),
  image_url: Type.Optional(
    Type.String({
      format: 'uri',
      deprecated: true,
      description: 'The URL of the chat image. Use public_picture_url or private_picture_download_url instead.',
    }),
  ),
  created_at: Type.Optional(
    Type.String({
      description: 'The creation date of the chat. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
    }),
  ),
  updated_at: Type.Optional(
    Type.String({
      description:
        'The last update date of the chat. Use ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ). Some providers update this field on new messages.',
    }),
  ),
  last_message_timestamp: Type.Optional(
    Type.String({
      description: 'The timestamp of the last message in the chat. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
    }),
  ),
  muted_until: Type.Union([Type.String(), Type.Boolean()], {
    description:
      'The date until the chat is muted. Use ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ). Is `true` if muted indefinitely.',
  }),
  creator_id: Type.Optional(Type.String({ description: 'The ID of the user who created the chat.' })),
  user_id: Type.Optional(Type.String({ description: 'The ID of the other participant in the chat if 1to1 chat.' })),
  is_group: Type.Boolean({ description: 'Is the chat a group chat.' }),
  is_1to1: Type.Boolean({ description: 'Is the chat a 1to1 chat.' }),
  is_channel: Type.Boolean({ description: 'Is the chat a channel chat.' }),
  type: chatTypeSchema,
  is_pinned: Type.Boolean({ description: 'Is the chat pinned at the top of the list, if supported by the provider.' }),
  is_readonly: Type.Boolean({
    description: 'Is the chat read-only for the current user. Trying to send messages in read-only chats will fail.',
  }),
  is_archived: Type.Boolean({ description: 'Is the chat archived. Archived chats are usually hidden from the chat list.' }),
  folders: Type.Array(Type.String(), { description: 'A list of folders the chat belongs to.' }),
  participants_count: Type.Optional(
    Type.Number({ minimum: 1, description: 'The number of participants in the chat if the chat is a group.' }),
  ),
  unread_count: Type.Number({
    minimum: -1,
    description:
      'The number of unread messages in the chat. `0` if the chat is read, `-1` if the chat was manually marked as unread by the user and does not have any unread messages.',
  }),
  restrictions: Type.Optional(
    Type.Array(chatPermissionSchema, {
      description: `List of action restrictions of the chat.
      - \`send_message\` is the permission to send messages.`,
    }),
  ),
  participants: Type.Optional(
    Type.Array(groupParticipantSchema, { description: 'List of participants in the chat if group chat.' }),
  ),
  last_message: Type.Optional(
    Type.Union([messagePreviewSchema, messageSchema], { description: 'The last message in the chat.' }),
  ),
  user: Type.Optional(Type.Object(minimalUserSchema, { description: 'The other participant if 1to1 chat.' })),
  provider: ProviderSchema,
  specifics: Type.Optional(Type.Any()),
});

export type ChatPermission = Static<typeof chatPermissionSchema>;
export type LastMessage = MessagePreview | Message;

export type Chat<Specifics = unknown, UserSpecifics = unknown> = Omit<
  Static<typeof chatSchema>,
  'specifics' | 'participants' | 'user'
> & {
  participants?: GroupParticipant<UserSpecifics>[];
  user?: MinimalUser<UserSpecifics>;
  specifics?: Specifics;
};
